import React, { useEffect, useContext, useState } from 'react';
import clsx from 'clsx';

import { cloneDeep } from 'lodash';

import {
    Grid,
    Typography,
    TextField,
    Button,
    FormControl,
    FormHelperText,
    CircularProgress
} from '@material-ui/core';

import {
    UploadFile
} from '../../../../common';

import { ToastContext } from '../../../../contexts/ToastContext';
import { PageContext } from '../../PageContext';

import { FileType, MaxFileSize } from '../../../../../helpers';

import { useTranslation } from "react-i18next";

import ClearIcon from '@material-ui/icons/Clear';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import HttpsIcon from '@material-ui/icons/Https';

import { makeStyles } from '@material-ui/core/styles';
const useStyles = makeStyles((theme) => ({
    root: {
    },
    navButton: {
        margin: theme.spacing(1),
    },
    progress: {
        marginRight: theme.spacing(1)
    }
}));

const MAX_STEPS = 2;

export const AppleCertificatesWizard = ({
    disabled,
    upload,
    onWizardComplete,
    onWizardCanceled,
    ...props }) => {
    const classes = useStyles();

    const { t, i18n } = useTranslation();

    const {
        state,
        isBusy
    } = useContext(PageContext);

    const [wizardValues, setWizardValues] = useState({
        appleUserEmail: { value: "", isValid: true, errorText: null },
        appleKeyId: { value: "", isValid: true, errorText: null },
        appleTeamId: { value: "", isValid: true, errorText: null },
        p8FileUrl: { value: "", isValid: true, errorText: null }
    });

    const {
        showSuccessMessage,
        showWarningMessage
    } = useContext(ToastContext);

    const {
        settings
    } = state;

    const validateWizardData = () => {
        let newValues = null;
        switch (currentStep) {
            case 0:
                newValues = {
                    appleUserEmail: checkWizardValue("appleUserEmail"),
                    appleKeyId: checkWizardValue("appleKeyId"),
                    appleTeamId: checkWizardValue("appleTeamId"),
                };
                break;
            case 1:
                newValues = {
                    p8FileUrl: checkWizardValue("p8FileUrl")
                };
                break;

            default:
                break;
        }

        if (newValues) {
            let isValid = true;
            Object.keys(newValues).forEach((key) => {
                if (!newValues[key].isValid)
                    isValid = false;
            });
            setWizardValues({
                ...cloneDeep(wizardValues),
                ...newValues
            });
            return isValid;
        } else {
            return false;
        }
    }

    const setWizardValue = (value, isValid, errorText) => {
        return { value, isValid: isValid ?? true, errorText: errorText ?? null }
    }

    const checkWizardValue = (valueName) => {
        return setWizardValue(
            wizardValues[valueName].value,
            wizardValues[valueName].value !== "",
            wizardValues[valueName].value === "" ? t("YupValueRequired") : null
        )
    }

    const uploadFileAsync = async (fileType, data, file, credentials) => {
        if (upload) {
            const storedFileInfo = await upload(fileType, data, file, credentials);
            if (storedFileInfo !== null) {
                switch (fileType) {
                    case FileType.iOSP8Certificate:
                        setWizardValues({ ...cloneDeep(wizardValues), p8FileUrl: setWizardValue(storedFileInfo.publicUrl) });
                        showSuccessMessage(t("MessageFileUploadOk"));
                        break;
                    default:
                        break;
                }
                return true;
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    const fileDropHandler = (fileType, file) => {
        let isFileOk = false;
        let wrongFileMessage = null
        if (file.size <= MaxFileSize.Max5MB) {
            switch (fileType) {
                case FileType.iOSP8Certificate:
                    isFileOk = file.name.includes(".p8");
                    wrongFileMessage = t("MessageiOSInvalidP8File");
                    break;
                default:
                    return false;
            }
        } else {
            showWarningMessage(t("MessageWrongFileSize5"));
            return false;
        }

        if (isFileOk) {
            return true;
        } else {
            showWarningMessage(wrongFileMessage);
            return false;
        }
    }

    const [currentStep, setStep] = useState(0);

    const stepBackClickHandler = (e) => {
        if (currentStep > 0)
            setStep(currentStep - 1);
    }

    const stepNextClickHandler = (e) => {
        if (!validateWizardData()) return;
        if (currentStep < MAX_STEPS - 1)
            setStep(currentStep + 1);
    }

    const canceledClickHandler = (e) => {
        if (onWizardCanceled) onWizardCanceled();
    }

    const completeClickHandler = (e) => {
        if (!validateWizardData()) return;
        if (onWizardComplete) {
            onWizardComplete(
                wizardValues.appleKeyId.value,
                wizardValues.appleTeamId.value,
                wizardValues.appleUserEmail.value,
                settings.bundleId
            );
        }
    }

    return (
        <Grid container spacing={3} className={clsx(classes.root, props.className)}>
            <Grid item xs={12}>
                <Typography variant="h5">
                    {t("LabelCertWizardTitle")}
                </Typography>
                <Typography variant="subtitle1">
                    {t("LabelStep")} {currentStep + 1} {t("LabelOf")} {MAX_STEPS}
                </Typography>
            </Grid>
            {currentStep === 0 &&
                <>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            error={!wizardValues.appleUserEmail.isValid}
                            helperText={wizardValues.appleUserEmail.errorText}
                            type="email"
                            variant="outlined"
                            label="Apple Developer Email"
                            disabled={disabled}
                            inputProps={{
                                autoComplete: 'new-email',
                                form: {
                                    autoComplete: 'off'
                                }
                            }}
                            value={wizardValues.appleUserEmail.value}
                            onChange={(e) => setWizardValues({ ...cloneDeep(wizardValues), appleUserEmail: setWizardValue(e.target.value) })}
                        />
                        <Typography variant="caption" component="div" style={{ marginTop: 3, marginLeft: 14, color: "#515151" }}>
                            {t("HelperAppleDevEmail")}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            error={!wizardValues.appleTeamId.isValid}
                            helperText={wizardValues.appleTeamId.errorText}
                            variant="outlined"
                            label="Apple Issuer ID"
                            disabled={disabled}
                            value={wizardValues.appleTeamId.value}
                            onChange={(e) => setWizardValues({ ...cloneDeep(wizardValues), appleTeamId: setWizardValue(e.target.value) })}
                        />
                        <Typography variant="caption" component="div" style={{ marginTop: 3, marginLeft: 14, color: "#515151" }}>
                            {t("HelperAppleDevIssuerId")}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            error={!wizardValues.appleKeyId.isValid}
                            helperText={wizardValues.appleKeyId.errorText}
                            variant="outlined"
                            label="Apple Key ID"
                            disabled={disabled}
                            value={wizardValues.appleKeyId.value}
                            onChange={(e) => setWizardValues({ ...cloneDeep(wizardValues), appleKeyId: setWizardValue(e.target.value) })}
                        />
                        <Typography variant="caption" component="div" style={{ marginTop: 3, marginLeft: 14, color: "#515151" }}>
                            {t("HelperAppleDevKeyId")}
                        </Typography>
                    </Grid>
                </>
            }
            {currentStep === 1 &&
                <>
                    <Grid item xs={12}>
                        {wizardValues.p8FileUrl.value ?
                            <Grid container item xs={12} alignItems="center" justify="space-between">
                                <HttpsIcon />
                                <Typography style={{ width: "85%" }}>
                                    {`${FileType.iOSP8Certificate}.p8`}
                                </Typography>
                                <Button
                                    className={classes.deleteButton}
                                    color="primary"
                                    variant="contained"
                                    disabled={disabled}
                                    size="small"
                                    onClick={() => setWizardValues({ ...cloneDeep(wizardValues), p8FileUrl: setWizardValue("", false) })}>
                                    {t("ButtonRemove")}
                                </Button>
                            </Grid> :
                            <FormControl error={!wizardValues.p8FileUrl.isValid} style={{ width: "100%" }}>
                                <UploadFile
                                    title={t("LabeliOSP8CertificateFile")}
                                    singleFile
                                    disabled={disabled}
                                    onFileDrop={(files) => fileDropHandler(FileType.iOSP8Certificate, files[0])}
                                    upload={(data, file) => uploadFileAsync(
                                        FileType.iOSP8Certificate,
                                        data,
                                        file
                                    )}
                                    helpText={t("LabelUploadHelp")}
                                    uploadButtonText={t("LabelUploadFile")}
                                    removeButtonText={t("LabelRemoveFile")}
                                    removeAllButtonText={"LabelRemoveAllFiles"}
                                />
                                <FormHelperText>
                                    {t("HelperAppleDevTokenFile")}
                                </FormHelperText>
                                <FormHelperText>
                                    {wizardValues.p8FileUrl.errorText}
                                </FormHelperText>
                            </FormControl>
                        }
                    </Grid>
                </>
            }
            <Grid container item justify="space-between">
                {currentStep === 0 &&
                    <Button
                        variant="contained"
                        startIcon={<ClearIcon />}
                        disabled={disabled}
                        onClick={canceledClickHandler}>
                        {t("ButtonCancel")}
                    </Button>
                }
                {currentStep > 0 &&
                    <Button
                        variant="contained"
                        startIcon={<NavigateBeforeIcon />}
                        disabled={disabled || currentStep == 0}
                        onClick={stepBackClickHandler}>
                        {t("ButtonBack")}
                    </Button>
                }
                {currentStep < MAX_STEPS - 1 &&
                    <Button
                        variant="contained"
                        endIcon={<NavigateNextIcon />}
                        disabled={disabled || currentStep == MAX_STEPS - 1}
                        onClick={stepNextClickHandler}>
                        {t("ButtonNext")}
                    </Button>
                }
                {currentStep === MAX_STEPS - 1 &&
                    <Button
                        variant="contained"
                        disabled={disabled}
                        onClick={completeClickHandler}>
                        {isBusy("creatingCerts") &&
                            <CircularProgress className={classes.progress} size={20} />
                        }
                        {t("ButtonGenerate")}
                    </Button>
                }
            </Grid>
        </Grid>
    );
}